import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { Users, MapPin, CheckCircle2, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

/**
 * /split/:delivery_id?token=... — public landing for a shared fare link.
 * · Unauth: shows the trip summary + a "Sign in to share the cost" CTA.
 * · Auth : shows "Split with me" button that joins as a co-payer, then
 *          redirects to the shared /ride/:id map so both parties can watch.
 */
export default function RideSplit() {
  const { delivery_id } = useParams();
  const [params]      = useSearchParams();
  const token         = params.get("token") || "";
  const { user }      = useAuth();
  const navigate      = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr]   = useState(null);
  const [busy, setBusy] = useState(false);

  useSeo({ title: "Split a ride — Find2Near" });

  useEffect(() => {
    api.get(`/delivery/${delivery_id}/split-info`, { params: { token } })
      .then(r => setInfo(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Link expired"));
  }, [delivery_id, token]);

  const join = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/delivery/${delivery_id}/split/join`, null, { params: { token } });
      navigate(`/ride/${delivery_id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not join");
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="max-w-md mx-auto p-8 text-center" data-testid="split-error">
        <div className="heading font-bold text-xl">{err}</div>
        <Link to="/services" className="mt-3 inline-flex text-sm font-bold text-[#E05E36]">Go to services →</Link>
      </div>
    );
  }
  if (!info) return <div className="p-8 text-stone-500 text-sm">Loading…</div>;

  return (
    <div className="max-w-md mx-auto px-4 md:px-6 py-8" data-testid="split-landing">
      <div className="bg-gradient-to-br from-sky-500 to-sky-700 text-white rounded-2xl p-5">
        <Users size={22} className="text-sky-100" />
        <div className="heading font-black text-2xl mt-1">Split this ride</div>
        <div className="text-xs text-sky-100 mt-1 capitalize">{info.service} · {info.distance_km} km</div>
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl p-4 mt-3 space-y-2 text-sm text-stone-700" data-testid="split-summary">
        <div className="flex items-center gap-2"><MapPin size={12} className="text-emerald-700" /> <b>Pickup:</b> {info.pickup?.address || "—"}</div>
        <div className="flex items-center gap-2"><MapPin size={12} className="text-rose-700" /> <b>Drop:</b> {info.dropoff?.address || "—"}</div>
        <div className="text-xs text-stone-500">Current payers: {info.payers.length} · your share ~{Math.round(info.per_person_share * 100)}%</div>
      </div>
      {user ? (
        <button
          onClick={join}
          disabled={busy}
          className="mt-4 w-full rounded-full bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="split-join"
        >
          <CheckCircle2 size={14} /> {busy ? "Joining…" : "Split with me"}
        </button>
      ) : (
        <Link to="/account" className="mt-4 w-full rounded-full bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 inline-flex items-center justify-center gap-2" data-testid="split-signin">
          <Share2 size={14} /> Sign in to share the cost
        </Link>
      )}
    </div>
  );
}
